import React, { useEffect, useState, useCallback, useMemo } from "react";
import { 
  Grid, 
  Column,
  Tooltip,
  TextArea,
  ToastNotification,
  TextInput,
  //DatePicker,
  //DatePickerInput,
  Checkbox,
  FormGroup
} from "@carbon/react";
import CommonFields from "../../common/CommonField";

const ResaleDetails = ({ 
  propertyUuid = "",
  data = {},
  attributeValues = [],
  isEditMode,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
  currentStep = 3,
  totalSteps = 7,
}) => {
  // ========== STATE MANAGEMENT ==========
  const [saving, setSaving] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [formError, setFormError] = useState(""); // Renamed from 'error' to avoid conflict
  const [touched, setTouched] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  const [showErrorToast, setShowErrorToast] = useState(false);
const [toastErrorMessage, setToastErrorMessage] = useState("");

  // ========== CONSTANTS ==========
  const fieldToAttributeMap = useMemo(() => ({
    expectedprice: "ren_resale_expected_price",
    //maintenancecost: "ren_resale_maintenance_cost",
    pricenegotiable: "ren_resale_price_negotiable",
    currentlyunderloan: "ren_resale_currently_under_loan",
    availablefrom: "ren_resale_available_from",
    kitchentype: "ren_resale_kitchen_type",
    furnishing: "ren_resale_furnishing",
    parking: "ren_resale_parking",
    description: "ren_resale_description"
  }), []);

  const validationRules = useMemo(() => ({
    expectedprice: {
      required: true,
      message: "Expected price is required",
      custom: (value) => {
        if (value && Number(value) <= 0) return "Expected price must be greater than 0";
        return null;
      },
    },
    // maintenancecost: {  
    //   required: true,
    //   message: "Maintenance cost is required",
    //   custom: (value) => {
    //     if (value && Number(value) < 0) return "Maintenance cost must be a positive number";
    //     return null;
    //   }
    // },
    furnishing: { 
      required: true, 
      message: "Furnishing is required" 
    },
    parking: { 
      required: true, 
      message: "Parking is required" 
    },
  }), []);

  // Form state matching JSON schema
  const [form, setForm] = useState({
    expectedprice: "",
   // maintenance : "",
    pricenegotiable: false,
    currentlyunderloan: false,
    availablefrom: "",
    kitchentype: "",
    furnishing: "",
    parking: "",
    description: ""
  });

  // ========== UTILITY FUNCTIONS ==========

  // Get filtered options for specific field from API data
  const getOptions = useCallback((fieldKey) => {
    const fieldName = fieldToAttributeMap[fieldKey];
    
    if (Array.isArray(attributeValues)) {
      return attributeValues.filter(
        (it) => it.attrName && it.isActive && it.attrName.toLowerCase().includes(fieldName.toLowerCase())
      );
    }
    return [];
  }, [attributeValues, fieldToAttributeMap]);


     const requiredLabel = (text) => (
  <>
    {text} <span style={{ color: "#da1e28" }}>*</span>
  </>
);

  // Update form field with new value
  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  const handleBlur = useCallback((fieldName) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
  }, []);

  // Get field error
  const getFieldError = useCallback((fieldName) => {
    const rule = validationRules[fieldName];
    if (!rule) return null;
    const val = form[fieldName];

    if (rule.required) {
      if (val === "" || val === null || val === undefined) return rule.message;
    }
    if (rule.custom && val !== "" && val !== null && val !== undefined) {
      return rule.custom(val);
    }
    return null;
  }, [form, validationRules]);

  // Check if form has errors
  const hasFieldErrors = useCallback(() => {
    const requiredFields = ["expectedprice", "furnishing", "parking"];
    return requiredFields.some((f) => !!getFieldError(f));
  }, [getFieldError]);

  // Build attributes payload for API
  const buildAttributesPayload = useCallback(() => {
    const attributesObject = {};

    // Fields that must always be sent (even when empty) so that
    // clearing them in the UI actually clears them on the server.
    const clearableFields = new Set([
      "description",
      "kitchentype",
      "furnishing",
      "parking",
      "availablefrom",
    ]);

    Object.keys(fieldToAttributeMap).forEach((fieldKey) => {
      const apiFieldName = fieldToAttributeMap[fieldKey];
      const val = form[fieldKey];

      // Booleans → always send "Yes" / "No"
      if (fieldKey === "pricenegotiable" || fieldKey === "currentlyunderloan") {
        attributesObject[apiFieldName] = val ? "Yes" : "No";
        return;
      }

      // Numbers → only send if a real value exists
      if (fieldKey === "expectedprice") {
        if (val !== "" && val !== undefined && val !== null) {
          attributesObject[apiFieldName] = String(val);
        }
        return;
      }

      // Clearable text fields → always send, even when empty
      if (clearableFields.has(fieldKey)) {
        attributesObject[apiFieldName] =
          val === undefined || val === null ? "" : String(val);
        return;
      }

      // Everything else → only send when non-empty
      if (val !== "" && val !== undefined && val !== null) {
        attributesObject[apiFieldName] = String(val);
      }
    });

    console.log("📤 attributesPayload being sent:", attributesObject);

    return attributesObject;
  }, [form, fieldToAttributeMap]);

  // ========== INITIALIZE FORM FROM DATA ==========
  useEffect(() => {
    if (!data || typeof data !== "object") return;
    
    const attributes = data.attributes || {};
    const updated = {};
    
    // Initialize all fields
    Object.keys(fieldToAttributeMap).forEach((fieldKey) => {
      const apiFieldName = fieldToAttributeMap[fieldKey];
      const value = attributes[apiFieldName];
      
      if (value !== undefined && value !== null) {
        // Handle boolean values
        if (fieldKey === "pricenegotiable" || fieldKey === "currentlyunderloan") {
          updated[fieldKey] = value === "Yes" || value === true || value === "true";
        }
        // Handle number values
        else if (fieldKey === "expectedprice") {
          updated[fieldKey] = Number(value) || "";
        }
        // Handle date values
        else if (fieldKey === "availablefrom") {
          updated[fieldKey] = value;
        }
        // Handle other values
        else {
          updated[fieldKey] = value;
        }
      } else {
        // Set default values for empty fields
        if (fieldKey === "expectedprice") updated[fieldKey] = "";
        else if (fieldKey === "pricenegotiable") updated[fieldKey] = false;
        else if (fieldKey === "currentlyunderloan") updated[fieldKey] = false;
        else if (fieldKey === "availablefrom") updated[fieldKey] = "";
        else if (fieldKey === "kitchentype") updated[fieldKey] = "";
        else if (fieldKey === "furnishing") updated[fieldKey] = "";
        else if (fieldKey === "parking") updated[fieldKey] = "";
        else if (fieldKey === "description") updated[fieldKey] = "";
      }
    });
    
    setForm(updated);
    setIsInitialized(true);
  }, [data, fieldToAttributeMap]);

  // ========== SAVE HANDLERS ==========

  // Save only (without navigation)
  const handleSaveOnly = async () => {
    console.log("🔄 handleSaveOnly called");
    
    // Mark all fields as touched
    const newTouched = {};
    Object.keys(form).forEach((k) => (newTouched[k] = true));
    setTouched(newTouched);

    // Validate
if (hasFieldErrors()) {
  const msg = "Please fill all required fields";
  setFormError(msg);
  setToastErrorMessage(msg);
  setShowErrorToast(true);
  return { success: false, error: "Validation failed" };
}

    if (!propertyUuid) {
      setFormError("Property UUID is missing. Please complete step 1 first.");
      return { success: false, error: "Property UUID missing" };
    }

    setSaving(true);
    setFormError("");
    setShowSuccessToast(false);

    try {
      const attributesPayload = buildAttributesPayload();
      console.log("📤 Saving attributes:", attributesPayload);
      
      // Call save function
      const result = await saveSectionAttributes(
        propertyUuid, 
        attributesPayload, 
        form, 
        "resale_detail"
      );
      
      setShowSuccessToast(true);
      
      // Call onSaveSuccess to update parent with fresh data
      if (onSaveSuccess) {
        onSaveSuccess(propertyUuid, result?.freshPropertyDetails || form, currentStep);
      }
      
      return { 
        success: true, 
        data: { property_uuid: propertyUuid }, 
        formData: form,
        freshData: result?.freshPropertyDetails 
      };
    } catch (err) {
      console.error("Save error:", err);
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to save resale details.";
      setFormError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setSaving(false);
    }
  };

  // Save and navigate to next
  const handleSaveAndNext = async () => {
    console.log("🔄 handleSaveAndNext called");
    const result = await handleSaveOnly();
    
    if (result.success) {
      // Navigate to next step
      if (onNavigateNext) {
        console.log("➡️ Navigating to next step");
        onNavigateNext(result.formData);
      }
    }
  };
  
  // Get CommonFields render functions
  const { 
    renderDropdown, 
    renderNumberField,
    renderErrorNotification 
  } = CommonFields({
    form,
    touched,
    getFieldError,
    handleBlur,
    updateField,
    getOptions,
  });

  // ========== DATE FORMATTING FUNCTIONS ==========
  const formatDateForInput = useCallback((dateString) => {
    if (!dateString) return "";
    // Convert from "dd/mm/yyyy" to Date object
    const parts = dateString.split('/');
    if (parts.length === 3) {
      return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
    }
    return null;
  }, []);

  const formatDateForStorage = useCallback((date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }, []);

  // Show loading while initializing
  if (!isInitialized) {
    return (
      <div style={{ 
        backgroundColor: 'white', 
        minHeight: '100vh', 
        padding: '2rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <p>Loading form data...</p>
      </div>
    );
  }

  // ========== RENDER COMPONENT ==========
return (
  <div>
{/* ERROR TOAST NOTIFICATION (top-right) */}
{showErrorToast && (
  <ToastNotification
    kind="error"
    title="Error"
    subtitle={toastErrorMessage}
    lowContrast
    onClose={() => setShowErrorToast(false)}
    style={{
      position: 'fixed',
      top: '1rem',
      right: '1rem',
      zIndex: 9999,
      minWidth: '350px',
    }}
    timeout={4000}
  />
)}

    {/* ERROR NOTIFICATION */}
    {formError && renderErrorNotification?.(formError, () => setFormError(""))}
<div style={{ textAlign: "center", marginBottom: "1rem" }}>

  <p
    style={{
      color: "#0f62fe",
      fontSize: "0.95rem",
      fontWeight: 500,
      maxWidth: "700px",
      margin: "0 auto",
      lineHeight: "1.5",
    }}
  >
    Provide additional details about your property to get maximum visibility
  </p>
</div>    {/* MAIN FORM */}
    <FormGroup>
      {/* ROW 1: PRICING SECTION */}
      <Grid>
        <Column sm={4} md={4} lg={4}>
          {renderNumberField(
            "expectedprice", 
            "Expected Price", 
            "0", 
            true
          )}
        </Column>

        {/* <Column sm={4} md={4} lg={4}>
          {renderNumberField(
            "maintenancecost", 
            "Maintenance Cost", 
            "0", 
            true
          )}
        </Column> */}

        <Column sm={4} md={4} lg={4}>
          <div style={{ paddingTop: '2rem' }}>
            <Checkbox
              id="pricenegotiable"
              labelText="Price Negotiable"
              checked={form.pricenegotiable}
              onChange={(e) => updateField("pricenegotiable", e.target.checked)}
            />
          </div>
        </Column>

        <Column sm={4} md={4} lg={4}>
          <div style={{ paddingTop: '2rem' }}>
            <Checkbox
              id="currentlyunderloan"
              labelText="Currently Under Loan"
              checked={form.currentlyunderloan}
              onChange={(e) => updateField("currentlyunderloan", e.target.checked)}
            />
          </div>
        </Column>
      </Grid>
    </FormGroup>

    <FormGroup>
      {/* ROW 2: AVAILABLE FROM & KITCHEN TYPE */}
      <Grid>
<Column sm={4} md={4} lg={4}>
  <TextInput
    id="availablefrom"
    type="date"
    labelText="Available From"
    value={form.availablefrom || ""}
    onChange={(e) => updateField("availablefrom", e.target.value)}
    onBlur={() => handleBlur("availablefrom")}
  />
</Column>

        <Column sm={4} md={4} lg={4}>
          {renderDropdown("kitchentype", "Kitchen Type", false)}
        </Column>
      </Grid>
    </FormGroup>

    <FormGroup>
      {/* ROW 3: FURNISHING & PARKING */}
      <Grid>
        <Column sm={4} md={4} lg={4}>
          <Tooltip label="Fully furnished- includes T.V., fridge and other white goods including mattresses, sofa set etc. Semi-furnished - includes cupboards, fans, lights and geysers.">
            {renderDropdown("furnishing", "Furnishing", true)}
          </Tooltip>
        </Column>

        <Column sm={4} md={4} lg={4}>
          <Tooltip label="Specify the type of parking available">
            {renderDropdown("parking", "Parking", true)}
          </Tooltip>
        </Column>
      </Grid>
    </FormGroup>

    <FormGroup>
      {/* ROW 4: DESCRIPTION */}
      <Grid>
        <Column sm={4} md={8} lg={16}>
          <TextArea
            id="description"
            labelText="Description"
            value={form.description}
            onChange={(e) => updateField("description", e.target.value)}
            onBlur={() => handleBlur("description")}
            placeholder="Write a few lines about your property something which is special and makes your property stand out. Please do not mention your contact details in any format."
            rows={4}
          />
        </Column>
      </Grid>
    </FormGroup>

    <FormGroup>
      {/* ROW 5: ACTION BUTTONS */}
      <Grid>
        <Column sm={4} md={8} lg={16}>
          <CommonButtons
            onSave={handleSaveOnly}
            onNext={handleSaveAndNext}
            showBack={true}
            showSave={true}
            showNext={true}
            isSaveLoading={saving}
            stepData={form}
          />
        </Column>
      </Grid>
    </FormGroup>
  </div>
);
};

export default ResaleDetails;